import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Select from 'react-select';

// Data
import { getAuthUser } from '../../../lib/services/auth.service';
import { buildRestoreMemberAccess, buildRevokeMemberAccess, getCurrMember } from '../../../lib/services/member.service';
import { MEMBER_FIELDS } from '../../../lib/queries/member.queries';
import { useRoleAllowedAssessments, getAssessments } from '../../../lib/services/assessment.service';

// Components
import { HighLevelMemberListContainer } from './high-level-member-list.container';
import { getAgeGroups } from '../../../lib/services/age-group.service';
import { AgeGroupMemberList } from './age-group-member-list.component';
import { Grid, Typography } from '@material-ui/core';
import { SectionWrapper } from '../../_core/_ui/structure.components';
import { Label, SectionHeading } from '../../_core/_ui/typography.component';
import { toggleListItem } from '../../../lib/helpers/data-structure.helpers';
import { useOperationErrorHandler } from '../../../lib/hooks/use-operation-error-handler';
import { createSingleFlightScope, useSingleFlight } from '../../../lib/hooks/use-single-flight';
import { readUserInputMessage } from '../../../lib/helpers/graphql-error.helpers';
import { writeScopedRoles } from './member-list.cache-utils';
import { roleScopeKey, roleScopeOf } from './member-list.helpers';
import { messages } from './member-list.messages';
import { IRoleScope, IToggleRoleParams, IUpdateMemberRolesParams, RoleScopeTarget } from './member-list.types';
import { ROLES } from '../../../../_configuration';

// Queries
const GET_MEMBER_LIST = gql`
  query GetMemberList($companyId: ID!) {
    members(companyId: $companyId) {
      ...MemberFields
    }
    countArchivedMembers(companyId: $companyId)
  }

  ${MEMBER_FIELDS}
`;

const GET_ARCHIVED_MEMBER_LIST = gql`
  query GetArchivedMemberList($companyId: ID!) {
    archivedMembers(companyId: $companyId) {
      ...MemberFields
    }
  }

  ${MEMBER_FIELDS}
`;

const UPDATE_MEMBER_ROLES = gql`
  mutation UpdateMemberRoles($_id: ID!, $ageGroupId: ID, $assessmentId: ID, $roles: [String!]!) {
    updateMemberRoles(_id: $_id, ageGroupId: $ageGroupId, assessmentId: $assessmentId, roles: $roles) {
      roles
    }
  }
`;

// Module-level, so leaving and returning to the page can't start a second save for a scope still in flight.
const ROLE_WRITES = createSingleFlightScope();

export const MemberListGraphQL = () => {
  const [viewingArchived, setViewingArchived] = useState(false);
  const user = getAuthUser();
  const { revokeMemberAccess } = buildRevokeMemberAccess();
  const { restoreMemberAccess } = buildRestoreMemberAccess();
  const { member: currMember } = getCurrMember();
  const [selectedUser, setSelectedUser] = useState<{value: string, label: string} | null>(null);

  // Listen for user selection events from other components
  useEffect(() => {
    const handleUserSelection = (event: any) => {
      const { userId, userName } = event.detail;
      setSelectedUser({ value: userId, label: userName });
    };

    window.addEventListener('selectUserForAgeGroup', handleUserSelection);
    return () => window.removeEventListener('selectUserForAgeGroup', handleUserSelection);
  }, []);

  const { data: sessionData, loading: sessionsLoading, error: sessionsError, refetch: refetchMembers } = useQuery(GET_MEMBER_LIST, {
    skip: !user || viewingArchived,
    variables: { companyId: user?.settings.activeCompanyId },
    fetchPolicy: 'cache-and-network',
    // Otherwise every role save refetches the whole list, and a refetch that lands after a later save rolls it back.
    nextFetchPolicy: 'cache-first'
  });

  const { data: archivedData, loading: archivedLoading, error: archivedError, refetch: refetchArchived } = useQuery(GET_ARCHIVED_MEMBER_LIST, {
    skip: !user || !viewingArchived,
    variables: { companyId: user?.settings.activeCompanyId },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first'
  });

  // Get assessments and filter by USER_MANAGEMENT permissions
  const { assessments: allAssessments } = getAssessments();
  const allowedAssessments = useRoleAllowedAssessments({
    assessments: allAssessments,
    requiredRoles: [ROLES.USER_MANAGEMENT]
  });

  // Check if current assessment is accessible for user management
  const contextAssessmentId = useMemo(() =>  user?.settings.activeAssessmentId, [user?.settings.activeAssessmentId]);

  // Get age groups for the current assessment context - no role filtering needed here
  const { ageGroups } = getAgeGroups();

  // Force re-evaluation when assessment changes by including contextAssessmentId in dependencies
  const contextualAgeGroups = useMemo(() => {
    // This will cause re-evaluation when contextAssessmentId changes
    return ageGroups;
  }, [ageGroups, contextAssessmentId]);

  const members: IMember[] = useMemo(() => viewingArchived
    ? (archivedData && archivedData.archivedMembers.filter((m: any) => "user" in m)) || []
    : (sessionData && sessionData.members) || [], [archivedData, sessionData, viewingArchived]);

  const { cache } = useApolloClient();
  const [updateMemberRolesMutation] = useMutation(UPDATE_MEMBER_ROLES);
  const reportRolesFailure = useOperationErrorHandler(messages.errRolesNotSaved);
  // Written straight to the cache, not as an optimisticResponse: an optimistic layer snapshots the whole
  // member and would hide another scope's save landing underneath it. One save per scope makes the rollback safe.
  const saveMemberRoles = (params: IUpdateMemberRolesParams) => {
    const scope = roleScopeOf(params);
    const { roles } = params;
    const previousRoles = writeScopedRoles(cache, { ...scope, roles });

    return updateMemberRolesMutation({
      variables: { _id: scope.memberId, ageGroupId: scope.ageGroupId, assessmentId: scope.assessmentId, roles },
      update: (updateCache, {data}) => {
        if (data?.updateMemberRoles) writeScopedRoles(updateCache, { ...scope, roles: data.updateMemberRoles.roles });
      },
    }).catch(error => {
      writeScopedRoles(cache, { ...scope, roles: previousRoles });
      const reason = readUserInputMessage(error);
      reportRolesFailure('updateMemberRoles', error, reason ? { message: messages.errRolesRefused, values: { reason } } : undefined);
      // The save may have landed with only its reply lost, so the rollback is corrected from the server.
      return (viewingArchived ? refetchArchived() : refetchMembers())
        .catch(refetchError => console.error('refetch after a failed role save failed:', refetchError));
    });
  };

  const roleWrites = useSingleFlight(saveMemberRoles, {
    scope: ROLE_WRITES,
    key: params => roleScopeKey(roleScopeOf(params)),
  });
  const handleUpdateMemberRoles = roleWrites.run;
  const { isInFlight } = roleWrites;
  const isSavingRoles = useCallback((scope: IRoleScope) => isInFlight(roleScopeKey(scope)), [isInFlight]);

  const countArchivedMembers = (sessionData && _.isNumber(sessionData.countArchivedMembers)) ? sessionData.countArchivedMembers : 0;
  const loading = !members.length && (sessionsLoading || archivedLoading);

  // Find the selected user object
  const selectedUserModel = useMemo(() => members.find(m => m._id === selectedUser?.value) || null, [members, selectedUser]);

  // Handler for the eye icon
  const handleViewAgeGroups = (userId: string) => {
    setSelectedUser({value: userId, label: members.find(m => m._id === userId)?.user.name || ''});
    // Optionally scroll/focus Age Group Access panel here
  };

  const toggleRole = useCallback(({member, role, ageGroupId, forceToggleResult, forcedOnRoles, isAssessmentRole}: IToggleRoleParams) => {
    // Without an assessment id the write would land on the association-level roles.
    if (isAssessmentRole && !ageGroupId && !contextAssessmentId) {
      console.warn('toggleRole: no active assessment, so the assessment-level role was not saved');
      return Promise.resolve(undefined);
    }

    let target: RoleScopeTarget = {};
    let sourceRoles: string[];
    if (ageGroupId) {
      target = { ageGroupId };
      sourceRoles = member.rolesByAgeGroup.find(r => r.ageGroupId === ageGroupId)?.roles ?? [];
    } else if (isAssessmentRole && contextAssessmentId) {
      target = { assessmentId: contextAssessmentId };
      sourceRoles = member.rolesByAssessment.find(r => r.assessmentId === contextAssessmentId)?.roles ?? [];
    } else {
      sourceRoles = member.superUserRoles ?? [];
    }

    let nextRoles = toggleListItem(sourceRoles, role, forceToggleResult);
    if (forcedOnRoles?.length) {
      for (let i = 0; i < forcedOnRoles.length; i++) {
        nextRoles = toggleListItem(nextRoles, forcedOnRoles[i], true)
      }
    }

    return handleUpdateMemberRoles({ member, roles: nextRoles, ...target });
  }, [contextAssessmentId, handleUpdateMemberRoles]);

  const canManageOrganization = useMemo(() => currMember?.superUserRoles?.includes(ROLES.USER_MANAGEMENT) || currMember?.isAdmin || false, [currMember?.superUserRoles, currMember?.isAdmin])

  const allowedAgeGroups = useMemo(() => {
    // If user has organization-wide USER_MANAGEMENT, they can manage all age groups
    if (canManageOrganization) return contextualAgeGroups;

    // For assessment-level users, show all age groups in their allowed assessments
    const assessmentAgeGroups = contextualAgeGroups.filter(ag =>
      allowedAssessments.some(assessment => assessment.ageGroupIds?.includes(ag._id))
    );

    // Also include age groups where user has explicit USER_MANAGEMENT role
    const directAgeGroups = contextualAgeGroups.filter(ag => {
      const hasRole = currMember?.rolesByAgeGroup?.some(r =>
        r.ageGroupId === ag._id && r.roles.includes(ROLES.USER_MANAGEMENT)
      );
      if (hasRole) {
      }
      return hasRole;
    });

    // Combine and deduplicate
    const combined = [...assessmentAgeGroups, ...directAgeGroups];
    const result = combined.filter((ag, index, self) =>
      index === self.findIndex(item => item._id === ag._id)
    );

    return result;
  }, [canManageOrganization, contextualAgeGroups, currMember?.rolesByAgeGroup, allowedAssessments])

  // Memoize user options to prevent unnecessary recalculations
  const userOptions = useMemo(() =>
    members.map(m => ({ value: m._id, label: m.user.name + ' (' + m.user.email + ')' })),
    [members]
  );

  return (
    <>
      {canManageOrganization && (
        <Grid item xs={12}>
          <SectionWrapper>
            <SectionHeading>General Permissions</SectionHeading>
            <HighLevelMemberListContainer
              key={contextAssessmentId} // Force re-render when assessment changes
              loading={loading}
              members={members}
              currMember={currMember}
              countArchivedMembers={countArchivedMembers}
              fetchError={Boolean(sessionsError) || Boolean(archivedError)}
              viewingArchived={viewingArchived}
              setViewingArchived={setViewingArchived}
              restoreMemberAccess={restoreMemberAccess}
              revokeMemberAccess={revokeMemberAccess}
              updateMemberRoles={handleUpdateMemberRoles}
              isSavingRoles={isSavingRoles}
              ageGroups={contextualAgeGroups}
              assessments={allowedAssessments}
              // Pass handler and state for Age Group Access quick-select
              onViewAgeGroups={handleViewAgeGroups}
              activeAgeGroupUserId={selectedUser?.value}
              assessmentId={contextAssessmentId ?? ''}
              toggleRole={toggleRole}
            />
          </SectionWrapper>
        </Grid>
      )}
      {/* Age Group Access Panel */}
      <Grid item xs={12}>
        <SectionWrapper>
          <SectionHeading>Age Group Permissions</SectionHeading>
          {(allowedAgeGroups.length > 0 || allowedAssessments.length > 0) && (
            <Box mb={2} width="300px">
              <Label>Select User</Label>
              <Select
                value={selectedUser}
                onChange={e => setSelectedUser(e)}
                options={userOptions}
                placeholder="Select a user..."
                isClearable
              />
            </Box>
          )}
          {/* Render Age Group Access table for selected user only */}
          {selectedUserModel && allowedAgeGroups.length === 0 && allowedAssessments.length > 0 && (
            <Typography variant="body2" style={{ color: '#666', fontStyle: 'italic', margin: '16px 0' }}>
              No age groups found for this assessment.
            </Typography>
          )}
          <AgeGroupMemberList
            key={`${contextAssessmentId}-${selectedUserModel?._id}`} // Force re-render when assessment or user changes
            currMember={selectedUserModel || undefined}
            updateMemberRoles={handleUpdateMemberRoles}
            isSavingRoles={isSavingRoles}
            toggleRole={toggleRole}
            ageGroups={selectedUserModel ? allowedAgeGroups : []}
            assessments={allAssessments}
            assessmentId={contextAssessmentId ?? ''}
          />
        </SectionWrapper>
      </Grid>
    </>
  );
};
